import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Banner } from '@/components/feedback/Banner'
import { Checkbox } from '@/components/ui/Checkbox'
import { Field } from '@/components/ui/Field'
import { FormSheet } from '@/components/ui/FormSheet'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { capabilityLabelKey, groupCapabilities } from '@/features/settings/capabilityLabels'
import { roleFieldError, roleRefusalMessage } from '@/features/settings/roleRefusals'
import { CAP } from '@/lib/can'
import { roleLabel } from '@/lib/navigation'
import type {
  Capability,
  CapabilityCatalogue,
  RoleChange,
  RoleRecord,
  ScopeLevel,
} from '@/types/domain'

/* The role editor — what a role is called, and what it may do.
 *
 * Two panels, because authorization here has two orthogonal axes and collapsing
 * them into one list is the mistake that makes a role screen lie:
 *
 *   CAPABILITIES — what the holder may DO. "May issue an invoice."
 *   ROW SCOPE    — whose records they may SEE. "Every case, or only their own."
 *
 * A role with `document.invoice.issue` and no `cases` scope can issue invoices
 * against nothing; a role with `all` on cases and no capabilities can read
 * everything and change none of it. Both are legitimate, and neither is
 * expressible if the screen only has checkboxes.
 *
 * The scope selector's fourth option is the important one. "No access" is not
 * the same as "own" — it means the surface is unreachable rather than filtered
 * — and it is the DEFAULT for a new role, because a role starts able to reach
 * nothing until somebody says otherwise.
 */

interface RoleFormSheetProps {
  open: boolean
  onClose: () => void
  /** The role being edited, or undefined when creating one. */
  role?: RoleRecord
  catalogue: CapabilityCatalogue
  onSubmit: (change: RoleChange) => void
  pending?: boolean
  /** The save's raw refusal, worded here by `roleRefusals.ts` — under the key,
   *  name or description field when it names one, in the banner otherwise. */
  failure?: unknown
}

/** The wire value for "this role does not reach this surface at all".
 *
 * An empty string rather than a level, because absence is not a level: the
 * server stores no row, and a row saying `none` would be a fourth level every
 * lookup would have to handle. The submit step drops these keys entirely. */
const NO_ACCESS = ''

export function RoleFormSheet({
  open,
  onClose,
  role,
  catalogue,
  onSubmit,
  pending,
  failure,
}: RoleFormSheetProps) {
  const { t } = useTranslation()
  const editing = role !== undefined

  const keyError = roleFieldError(failure, 'key', t)
  const nameError = roleFieldError(failure, 'name', t)
  const descriptionError = roleFieldError(failure, 'description', t)

  /* The fields hold what is STORED, never what the list shows. The list
   * translates a seeded role's name by its key and its description while it is
   * still the seed; seeding these inputs with either translation would write it
   * back on the first save of anything else, and from then on the seed would
   * read as an admin's own words in every language. */
  const [key, setKey] = useState(role?.key ?? '')
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')

  /* What the interface calls this role — the title, and the hint under the
   * name. A seeded role is named by its key everywhere else, so renaming one
   * shows only here, and the hint says so rather than letting the rename look
   * lost. */
  const shownAs = role ? roleLabel(role.key, role.name) : undefined
  const shownName = shownAs ? (shownAs.translated ? t(shownAs.key) : shownAs.text) : ''
  const [capabilities, setCapabilities] = useState<Set<Capability>>(
    () => new Set(role?.capabilities ?? []),
  )
  const [scopes, setScopes] = useState<Record<string, string>>(() => ({ ...(role?.scopes ?? {}) }))

  /* Capabilities grouped by their first dotted segment — quote.*, case.*,
   * directory.* — so the editor reads as sections rather than as forty
   * checkboxes in registration order. `groupCapabilities` is the one grouping
   * the user card's «Права роли» reads too; the items are looked up again by
   * key because the editor also needs each one's server description. */
  const groups = useMemo(() => {
    const byKey = new Map(catalogue.items.map((item) => [item.key, item]))
    return groupCapabilities(catalogue.items.map((item) => item.key)).map(
      ([prefix, keys]) =>
        [prefix, keys.flatMap((key) => byKey.get(key) ?? [])] as const,
    )
  }, [catalogue.items])

  function toggle(capability: Capability) {
    setCapabilities((current) => {
      const next = new Set(current)
      if (next.has(capability)) next.delete(capability)
      else next.add(capability)
      return next
    })
  }

  /* The superuser's capability list and row scope are not editable, because
   * the flag rather than either is what grants access — an edit would be a
   * no-op that looked like it worked. The server refuses both (the scope as
   * the field code `superuser-scopes-fixed`, story B3); the form says so
   * rather than offering forty checkboxes and six selectors whose answers
   * would be discarded. */
  const superuserLocked = role?.isSuperuser ?? false

  /* Changing legs without seeing counterparties (#133). The server serves
   * every saved leg to such a role with its carriers and drivers hidden, and
   * the leg page opens that leg read-only, so the right does far less than its
   * label says. Said where the box is ticked, as the boxes stand — decided by
   * the two KEYS, never a role name; it shows on the seeded Sales too, which is
   * true of it. The superuser's flag passes both checks. A warning, not a
   * refusal: the save goes through. */
  const legsNeedCounterparties =
    !superuserLocked &&
    capabilities.has(CAP.caseRouteEdit) &&
    !capabilities.has(CAP.counterpartyRead)

  function submit() {
    // Surfaces set to "no access" are DROPPED rather than sent as a level. A
    // missing key is how the server stores "unreachable"; sending one would
    // either be rejected or, worse, stored as the narrowest real level.
    const chosen: Record<string, ScopeLevel> = {}
    for (const [surface, level] of Object.entries(scopes)) {
      if (level !== NO_ACCESS) chosen[surface] = level as ScopeLevel
    }

    onSubmit({
      key: key.trim(),
      name: name.trim(),
      description: description.trim(),
      capabilities: [...capabilities],
      // No map for the superuser: the server reads an absent map as
      // "unchanged" and refuses one that narrows its scope.
      scopes: superuserLocked ? {} : chosen,
    })
  }

  return (
    <FormSheet
      open={open}
      onClose={onClose}
      title={editing ? t('settings.roles.edit', { name: shownName }) : t('settings.roles.create')}
      onSubmit={submit}
      pending={pending}
      disabled={key.trim() === '' || name.trim() === ''}
      error={failure ? roleRefusalMessage(failure, t) : undefined}
      notice={superuserLocked ? t('settings.roles.superuserLocked') : undefined}
    >
      <Field
        label={t('settings.roles.fields.key')}
        hint={t('settings.roles.fields.keyHint')}
        error={keyError}
        required
      >
        {(props) => (
          <Input
            value={key}
            onChange={(e) => setKey(e.target.value)}
            /* A system role's key is fixed: renaming `sales` would break every
               translation and fixture that names it, and the server refuses
               it. Disabled rather than absent so the founder can still SEE the
               key they are editing. */
            disabled={role?.isSystem ?? false}
            placeholder="ops-lead"
            {...props}
          />
        )}
      </Field>

      <Field
        label={t('settings.roles.fields.name')}
        hint={
          shownAs?.translated
            ? t('settings.roles.fields.nameSeededHint', { label: shownName })
            : undefined
        }
        error={nameError}
        required
      >
        {(props) => (
          <Input value={name} onChange={(e) => setName(e.target.value)} {...props} />
        )}
      </Field>

      <Field
        label={t('settings.roles.fields.description')}
        hint={t('settings.roles.fields.descriptionHint')}
        error={descriptionError}
      >
        {(props) => (
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            {...props}
          />
        )}
      </Field>

      {/* ── row scope ───────────────────────────────────────────────────── */}
      {!superuserLocked && (
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-medium text-text-heading">
              {t('settings.roles.scopes.title')}
            </h3>
            <p className="text-xs text-text-muted">{t('settings.roles.scopes.hint')}</p>
          </div>

          {catalogue.surfaces.map((surface) => (
            <Field key={surface} label={t(`settings.roles.surfaces.${surface}` as never, surface)}>
              {(props) => (
                <Select
                  value={scopes[surface] ?? NO_ACCESS}
                  onChange={(value) => setScopes((current) => ({ ...current, [surface]: value }))}
                  options={[
                    // Listed FIRST and the default for an unset surface: a role
                    // reaches nothing until somebody grants it.
                    { value: NO_ACCESS, label: t('settings.roles.levels.none') },
                    ...catalogue.levels.map((level) => ({
                      value: level,
                      label: t(`settings.roles.levels.${level}` as never, level),
                    })),
                  ]}
                  {...props}
                />
              )}
            </Field>
          ))}
        </section>
      )}

      {/* ── capabilities ────────────────────────────────────────────────── */}
      {!superuserLocked && (
        <section className="space-y-4">
          <div>
            <h3 className="text-sm font-medium text-text-heading">
              {t('settings.roles.capabilities.title')}
            </h3>
            <p className="text-xs text-text-muted">{t('settings.roles.capabilities.hint')}</p>
          </div>

          {groups.map(([prefix, items]) => (
            <fieldset key={prefix} className="space-y-2">
              <legend className="text-xs font-medium uppercase tracking-wide text-text-muted">
                {t(`settings.roles.groups.${prefix}` as never, prefix)}
              </legend>
              {items.map((item) => {
                // The catalogue's words for a key it knows; the server's own
                // description for one it does not (a server deployed ahead).
                const labelKey = capabilityLabelKey(item.key)
                /* Managing roles is the superuser's alone for now (S-7, O8):
                   a role holding it could grant itself everything, so no role
                   may be given it and the server refuses it as `reserved`. The
                   box keeps what is stored and cannot move. */
                const reserved = item.key === CAP.adminRolesManage
                return (
                  <Fragment key={item.key}>
                    <Checkbox
                      label={labelKey ? t(labelKey) : item.description}
                      hint={reserved ? t('settings.roles.superuserOnly') : item.key}
                      checked={capabilities.has(item.key)}
                      disabled={reserved}
                      onCheckedChange={() => toggle(item.key)}
                    />
                    {item.key === CAP.caseRouteEdit && legsNeedCounterparties ? (
                      <Banner
                        tone="attention"
                        title={t('settings.roles.warnings.legsNeedCounterparties.title')}
                      >
                        {t('settings.roles.warnings.legsNeedCounterparties.body', {
                          edit: t('settings.roles.capabilityLabels.caseRouteEdit'),
                          read: t('settings.roles.capabilityLabels.counterpartyRead'),
                        })}
                      </Banner>
                    ) : null}
                  </Fragment>
                )
              })}
            </fieldset>
          ))}
        </section>
      )}
    </FormSheet>
  )
}
